import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";

/**
 * App Update Checker for Live Updates with Version Control
 *
 * When using Capacitor with server.url (remote mode), WebView caching
 * can prevent users from getting updates. This hook checks version.json
 * to determine if updates should be forced.
 *
 * Version.json Controls:
 * - forceUpdate: true → Force immediate reload (critical fixes)
 * - version changed → Reload after background timeout (normal updates)
 * - minVersion → Force update if current version is too old
 *
 * Update Strategies:
 * 1. UI-only changes: Leave forceUpdate: false, users get update eventually
 * 2. Logic fixes: Set forceUpdate: true, users get update on next resume
 * 3. Breaking changes: Bump minVersion, force update immediately
 */

interface VersionInfo {
  version: string;
  buildTime: string;
  forceUpdate: boolean;
  minVersion: string;
}

/**
 * Get a unique identifier for this build.
 * Uses buildTime as the version identifier since version number
 * in package.json doesn't auto-increment.
 */
function getBuildIdentifier(versionInfo: VersionInfo): string {
  // Use buildTime as unique identifier
  return versionInfo.buildTime;
}

const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour in ms
const CURRENT_BUILD_KEY = "app_current_build"; // Stores buildTime instead of version

/**
 * Fetch version.json from server
 */
async function fetchVersionInfo(): Promise<VersionInfo | null> {
  try {
    const response = await fetch("/version.json?t=" + Date.now()); // Cache bust
    if (!response.ok) throw new Error("Version fetch failed");
    return await response.json();
  } catch (error) {
    console.error("[AppUpdates] Failed to fetch version:", error);
    return null;
  }
}

/**
 * Check if app needs update based on version info
 */
async function checkForUpdates(): Promise<"force" | "suggested" | "none"> {
  const versionInfo = await fetchVersionInfo();
  if (!versionInfo) return "none";

  // Use buildTime as the unique identifier (since version in package.json doesn't auto-increment)
  const currentBuild = localStorage.getItem(CURRENT_BUILD_KEY);
  const serverBuild = getBuildIdentifier(versionInfo);

  console.log("[AppUpdates] Build check:", {
    currentBuild,
    serverBuild,
    version: versionInfo.version,
    forceUpdate: versionInfo.forceUpdate,
    minVersion: versionInfo.minVersion,
  });

  // First launch - just store build identifier
  if (!currentBuild) {
    localStorage.setItem(CURRENT_BUILD_KEY, serverBuild);
    return "none";
  }

  // Check if we're already on the server build
  const isUpToDate = currentBuild === serverBuild;

  if (isUpToDate) {
    // Already on this build - no update needed (even if forceUpdate is true)
    // This prevents users from reloading repeatedly after they've already updated
    return "none";
  }

  // We have a different build than server - need to update

  // Check if server has force update flag (critical fixes)
  // This only triggers if user is on OLD build and server has forceUpdate: true
  if (versionInfo.forceUpdate) {
    console.log(
      "[AppUpdates] Server requested force update for new build:",
      serverBuild,
    );
    return "force";
  }

  // Build changed but no force flag - normal update
  console.log("[AppUpdates] New build available:", serverBuild);
  return "suggested";
}

/**
 * Perform app reload with logging
 */
function reloadApp(reason: string) {
  console.log(`[AppUpdates] Reloading app: ${reason}`);

  // Update stored build identifier before reload
  fetchVersionInfo().then(versionInfo => {
    if (versionInfo) {
      const buildId = getBuildIdentifier(versionInfo);
      localStorage.setItem(CURRENT_BUILD_KEY, buildId);
      console.log("[AppUpdates] Stored new build identifier:", buildId);
    }
  });

  // Small delay to let logs flush and other handlers finish
  setTimeout(() => {
    window.location.reload();
  }, 500);
}

export function useAppUpdates() {
  useEffect(() => {
    // Only run on native platforms with remote server
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let lastBackgroundTime: number | null = null;

    // Check for updates on initial mount
    checkForUpdates().then(updateStatus => {
      if (updateStatus === "force") {
        reloadApp("Force update on mount");
      }
    });

    // addListener returns a promise that resolves to the handle
    const setupListener = async () => {
      const listener = await CapacitorApp.addListener(
        "appStateChange",
        async ({ isActive }) => {
          if (!isActive) {
            // App went to background - record time
            lastBackgroundTime = Date.now();
            console.log(
              "[AppUpdates] App backgrounded at",
              new Date().toISOString(),
            );
          } else {
            // App came to foreground
            console.log(
              "[AppUpdates] App resumed at",
              new Date().toISOString(),
            );

            // Check for updates on resume
            const updateStatus = await checkForUpdates();

            if (updateStatus === "force") {
              // Critical update - reload immediately
              reloadApp("Force update on resume");
              return;
            }

            if (updateStatus === "suggested" && lastBackgroundTime) {
              const timeInBackground = Date.now() - lastBackgroundTime;
              console.log(
                "[AppUpdates] Time in background:",
                Math.floor(timeInBackground / 1000),
                "seconds",
              );

              // If app was in background for > 1 hour AND new version available, reload
              if (timeInBackground > UPDATE_CHECK_INTERVAL) {
                reloadApp("Suggested update after background timeout");
                return;
              }
            }

            // Fallback: If app was in background for very long (> 6 hours), force reload anyway
            if (
              lastBackgroundTime &&
              Date.now() - lastBackgroundTime > 6 * 60 * 60 * 1000
            ) {
              reloadApp("Long background timeout (> 6 hours)");
              return;
            }

            lastBackgroundTime = null;
          }
        },
      );

      console.log("[AppUpdates] Update checker initialized");
      return listener;
    };

    let listenerHandle: Awaited<
      ReturnType<typeof CapacitorApp.addListener>
    > | null = null;
    setupListener().then(handle => {
      listenerHandle = handle;
    });

    return () => {
      listenerHandle?.remove();
    };
  }, []);
}
